import styled from 'styled-components';

export const ScannerContainer = styled.div`
  background: var(--bg-section);
  min-height: calc(100vh - 68px);
  padding: 40px 20px 72px;
`;

export const ScannerCard = styled.div`
  max-width: 860px;
  margin: 0 auto;
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  padding: 32px;

  > p {
    color: var(--text-secondary);
    font-size: var(--text-sm);
    margin-bottom: 24px;
  }

  @media (max-width: 560px) {
    padding: 22px 16px;
  }
`;

export const ScannerTitle = styled.h1`
  font-size: var(--text-2xl);
  letter-spacing: -0.5px;
  margin-bottom: 4px;
`;

export const ScannerSection = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 24px;
  margin-bottom: 28px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const ScannerArea = styled.div`
  background: var(--bg-section);
  border: 2px dashed var(--border-medium);
  border-radius: var(--radius-lg);
  padding: 20px;
  text-align: center;

  #qr-reader {
    border-radius: var(--radius-md);
    overflow: hidden;
  }

  .scanner-placeholder {
    padding: 32px 12px;

    .scanner-icon {
      width: 56px;
      height: 56px;
      margin: 0 auto 10px;
      padding: 10px;
      background: var(--bg-dark);
      border-radius: var(--radius-md);

      img { width: 100%; height: 100%; display: block; }
    }

    p {
      color: var(--text-muted);
      font-size: var(--text-sm);
    }
  }

  .scanner-controls {
    margin-top: 14px;
  }
`;

export const ManualInput = styled.div`
  background: var(--bg-section);
  border-radius: var(--radius-lg);
  padding: 20px;

  h3 {
    font-size: var(--text-base);
    margin-bottom: 6px;
  }

  p {
    color: var(--text-secondary);
    font-size: var(--text-xs);
    margin-bottom: 12px;
  }

  .manual-input-group {
    display: flex;
    flex-direction: column;
    gap: 10px;

    input {
      width: 100%;
      padding: 11px 13px;
      background: var(--bg-card);
      border: 1.5px solid var(--border-light);
      border-radius: var(--radius-md);
      color: var(--text-primary);
      font-family: monospace;
      font-size: var(--text-xs);

      &:focus {
        outline: none;
        border-color: var(--border-focus);
      }
    }
  }
`;

export const ResultSection = styled.div`
  background: var(--primary-subtle);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 24px;
  margin-bottom: 28px;
`;

export const QRResult = styled.div`
  margin-bottom: 16px;

  h3 {
    font-size: var(--text-lg);
    color: var(--primary-dark);
    margin-bottom: 4px;
  }

  p {
    color: var(--text-secondary);
    font-size: var(--text-sm);
  }
`;

export const ReservationDetails = styled.div`
  background: var(--bg-card);
  border-radius: var(--radius-md);
  padding: 16px 20px;
  margin-bottom: 18px;

  .detail-row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 7px 0;
    font-size: var(--text-sm);
    border-bottom: 1px solid var(--border-light);

    &:last-child { border-bottom: none; }

    span:first-child { color: var(--text-secondary); }
    span:last-child {
      font-weight: 600;
      text-align: right;
      min-width: 0;
      overflow-wrap: anywhere;
    }
  }
`;

export const ValidationButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  button { flex: 1 1 160px; }

  @media (max-width: 560px) {
    flex-direction: column;

    button { flex: none; width: 100%; }
  }
`;

export const RecentScans = styled.div`
  h3 {
    font-size: var(--text-lg);
    margin-bottom: 14px;
  }
`;

export const ScanItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  background: var(--bg-section);
  border-radius: var(--radius-md);
  margin-bottom: 8px;

  .scan-info {
    min-width: 0;

    .scan-customer {
      font-size: var(--text-sm);
      font-weight: 600;
    }

    .scan-details {
      font-size: var(--text-xs);
      color: var(--text-secondary);
    }

    .scan-time {
      font-size: var(--text-xs);
      color: var(--text-muted);
    }
  }

  .scan-status {
    font-size: var(--text-xs);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }
`;

export const CameraBox = styled.div`
  position: relative;
  border-radius: var(--radius-md);
  overflow: hidden;
  background: ${props => (props.$active ? 'var(--bg-dark)' : 'transparent')};

  #qr-reader {
    width: 100%;
    border: none !important;
  }

  #qr-reader video {
    width: 100% !important;
    max-width: 100%;
    display: block;
    object-fit: cover;
  }

  .camera-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--white);
    font-size: var(--text-sm);
    padding: 16px;
    text-align: center;
  }
`;

export const CameraMessage = styled.div`
  padding: 24px 12px 8px;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  line-height: 1.5;
  overflow-wrap: anywhere;

  .scanner-icon {
    width: 48px;
    height: 48px;
    margin: 0 auto 8px;
    padding: 8px;
    background: var(--bg-dark);
    border-radius: var(--radius-md);

    img { width: 100%; height: 100%; display: block; }
  }
`;

export const AssignedPanel = styled.div`
  background: var(--primary-subtle);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  padding: 14px 16px;
  margin-bottom: 20px;
  min-width: 0;

  .assigned-head {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 8px 12px;
    margin-bottom: 8px;
  }

  h3 {
    font-size: var(--text-base);
    overflow-wrap: anywhere;
    min-width: 0;
  }

  .assigned-role {
    color: var(--text-secondary);
    font-size: var(--text-xs);
  }

  ul {
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 0;
    padding: 0;
  }

  li {
    background: var(--bg-card);
    border-radius: var(--radius-round);
    padding: 4px 12px;
    font-size: var(--text-xs);
    font-weight: 600;
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  .assigned-empty {
    color: var(--danger-dark);
    font-size: var(--text-sm);
  }
`;

export const Feedback = styled.div`
  border-radius: var(--radius-lg);
  padding: 20px;
  margin-bottom: 28px;
  min-width: 0;
  overflow-wrap: anywhere;
  background: ${props => (props.$tone === 'success' ? 'var(--success-subtle)' : props.$tone === 'danger' ? 'var(--danger-subtle)' : 'var(--bg-section)')};
  border: 1px solid ${props => (props.$tone === 'success' ? 'var(--success-light)' : props.$tone === 'danger' ? 'var(--danger-light)' : 'var(--border-light)')};

  h3 {
    font-size: var(--text-lg);
    margin-bottom: 6px;
    color: ${props => (props.$tone === 'success' ? 'var(--success-dark)' : props.$tone === 'danger' ? 'var(--danger-dark)' : 'var(--text-primary)')};
  }

  p {
    color: var(--text-secondary);
    font-size: var(--text-sm);
    line-height: 1.5;
  }

  .feedback-actions {
    margin-top: 14px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .feedback-assigned {
    margin-top: 12px;
    font-size: var(--text-sm);
    color: var(--text-secondary);

    strong { color: var(--text-primary); }
  }
`;

export const ScannedWarning = styled.div`
  color: var(--danger-dark);
  font-weight: 700;
  font-size: var(--text-sm);
  margin-top: 10px;
`;
